// Colour themes a player can choose in the profile (designed in the theme studio artifact).
// Each theme gives the same colour tokens app.css uses, for light and dark mode, plus the corner
// radius. The first one is the app's own look; its values match the defaults in app.css.

/** The tokens a theme sets, in the order the compact palettes below list them. */
export const THEME_TOKENS = ['frost', 'paper', 'paper-2', 'line', 'ink', 'ink-2', 'ink-3', 'felt', 'felt-2', 'felt-deep', 'walnut', 'walnut-2', 'brass', 'brass-soft', 'primary', 'primary-ink', 'ok', 'danger', 'card-face', 'suit-s', 'suit-h', 'suit-d', 'suit-c', 'chat-bg', 'bubble-mine', 'bubble-theirs'] as const;
export type ThemeToken = (typeof THEME_TOKENS)[number];
export type Palette = Record<ThemeToken, string>;

export interface Theme {
  id: string;
  name: string;
  note: string;
  radius: number;
  light: Palette;
  dark: Palette;
}

const pal = (s: string): Palette => {
  const hex = s.trim().split(/\s+/);
  if (hex.length !== THEME_TOKENS.length) throw new Error('palette has the wrong number of colours');
  return Object.fromEntries(THEME_TOKENS.map((k, i) => [k, hex[i]!])) as Palette;
};

const T = (id: string, name: string, note: string, radius: number, light: string, dark: string): Theme => ({ id, name, note, radius, light: pal(light), dark: pal(dark) });

export const THEMES: Theme[] = [
  T(
    'nykyinen',
    'Alkuperäinen',
    'Sovelluksen alkuperäinen ilme: petrooliveka ja pähkinäreunus.',
    10,
    '#edf0f3 #ffffff #f6f8fa #d5dbe1 #17202a #4a5663 #6b7683 #0f4a4f #13595f #0b3a3e #3a2a20 #5a4130 #b8862b #f5ead2 #0f4a4f #ffffff #1e6b44 #b3261e #fdfdfb #17202a #c0392b #1f5fbf #1e7b4a #e7ecee #d4efe9 #ffffff',
    '#11171c #182028 #1d2630 #2c3743 #e7ecf0 #b2bcc6 #8c97a3 #145a60 #196a71 #0d3f44 #3a2a20 #5a4130 #d4a24a #3a2f1a #1f7479 #ffffff #6fcf97 #ef7d74 #fdfdfb #17202a #c0392b #1f5fbf #1e7b4a #0d1317 #134a4c #1f2a33',
  ),
  T(
    'monte-carlo',
    'Monte Carlo',
    'Klassinen kasino: vihreä veka, viininpunainen reunus, kulta.',
    8,
    '#eef0ea #ffffff #f5f6f1 #d6dbd0 #1b211c #475046 #6b7468 #1d5a3a #246b46 #143f29 #4a1f1a #6b2c24 #b89233 #f3ecd6 #1d5a3a #ffffff #2f7a4b #a4262c #fffdf7 #1b211c #b3261e #1f5fbf #2f7a4b #e8ebe3 #d6ecd9 #ffffff',
    '#0f1511 #17201a #1c271f #2b3a2f #e9eee8 #b5c0b3 #8a9788 #1f6a43 #26794d #164d31 #3d1a16 #5a2620 #d6ae55 #3a3120 #3c9a66 #07130b #6fcf97 #ef7d74 #fbfaf5 #1b211c #b3261e #1f5fbf #2f7a4b #0c120e #1d4a31 #1f2a22',
  ),
  T(
    'keskiyo',
    'Keskiyö',
    'Sininen veka ja meripihka: myöhäisten iltapelien tunnelma.',
    12,
    '#eceff5 #ffffff #f4f6fa #d5dae4 #141a2a #454f66 #69738a #1c3f73 #234c88 #142f58 #1b1f2b #2c3346 #e0a130 #fbefd6 #1c3f73 #ffffff #23794f #c2362b #fdfdfd #141a2a #d23a2e #2f6fe0 #1f8a55 #e6eaf2 #d8e3f7 #ffffff',
    '#0b0f19 #131a28 #182032 #263049 #e8ecf5 #aeb8cc #8591a8 #1f4a86 #2658a0 #163866 #0e111a #20263a #f2b441 #3a2f18 #4f86e0 #07101f #6fd39c #f07b70 #f7f8fb #141a2a #d23a2e #2f6fe0 #1f8a55 #080c14 #1c3b6b #1a2334',
  ),
  T(
    'mokki',
    'Mökki',
    'Kuusenvihreä veka, mäntyreunus ja puolukanpunainen korostus.',
    14,
    '#ecebe7 #fffefb #f6f4ef #dcd7cc #2a2420 #5a5048 #7d7268 #2f5d4e #386e5c #234639 #6b4a2e #8a6440 #b5402f #f6e3dd #2f5d4e #ffffff #3a7a4e #a52a1f #fffdf8 #2a2420 #b5402f #2e6aa6 #3a7a4e #e9e6df #dfeadf #ffffff',
    '#15120f #1f1a16 #26201b #3a3129 #f0ebe4 #c4b9ab #9a8e80 #2f6a57 #377a64 #234f41 #4a3320 #654630 #e0735f #3d2420 #4f9a80 #0b1611 #7fcf98 #f08070 #fbf7f0 #2a2420 #b5402f #2e6aa6 #3a7a4e #100d0b #25453a #2a231d',
  ),
  T(
    'pohjoisvalo',
    'Pohjoisvalo',
    'Arktinen sininen veka ja revontulten vihreä korostus.',
    16,
    '#e9f0f2 #ffffff #f3f8f9 #d0dde1 #102229 #3f5760 #637b84 #28506e #2f5f82 #1d3b52 #22313b #34495a #1f9e78 #dcf3ea #28506e #ffffff #1f8a67 #c23b4e #fcfeff #102229 #d0404f #3b6fd4 #1f8a67 #e4ecef #d3efe6 #ffffff',
    '#0a1216 #111c22 #16242b #22353e #e6f1f3 #a9c0c7 #7f99a1 #2c5a7c #336890 #20445e #0f181e #1e2c35 #4fd6a8 #173a30 #5aa0c8 #04131b #6fdcb0 #f07a8a #f4fafb #102229 #d0404f #3b6fd4 #1f8a67 #071014 #184338 #16232a',
  ),
  T(
    'punainen-huone',
    'Punainen huone',
    'Punainen veka, mustalakattu reunus ja vanha kulta.',
    6,
    '#eeebea #ffffff #f7f4f3 #ddd5d3 #221a1a #574a49 #7a6d6c #7a1f24 #8c262c #5c161a #1f1a19 #3a3130 #c9a24a #f5ecd4 #7a1f24 #ffffff #2e7a4f #b3261e #fffdfb #221a1a #c0392b #2c5fb3 #2e7a4f #ebe6e5 #f1dcdc #ffffff',
    '#120e0e #1c1616 #231c1c #372c2b #f1eaea #c7b8b7 #9b8b8a #8a2429 #9c2c31 #66191d #0c0a0a #2a2222 #e2bd62 #3a311b #c4474d #ffffff #72cf98 #f07d74 #faf6f4 #221a1a #c0392b #2c5fb3 #2e7a4f #0d0a0a #4a2225 #2a2121',
  ),
  T(
    'vegas',
    'Vegas',
    'Violetti veka, musta reunus ja kasinon kulta.',
    12,
    '#f2eff6 #ffffff #f7f5fa #ddd6e6 #1d1528 #52466a #776b8c #4a2a80 #5a3598 #361d5f #16111f #2c2340 #d9a21b #fbf0d2 #4a2a80 #ffffff #1f7a53 #c21f5b #fffdfa #1d1528 #d42a4c #2f5fd6 #1f8a5a #ebe7f1 #e6dcf6 #ffffff',
    '#0e0a14 #17111f #1d1628 #30263f #f0ebf7 #bfb3d1 #9486aa #5b33a0 #6a3db8 #432679 #0a070f #221a30 #f2bd3a #3b301a #a07ce6 #140b24 #6fd6a4 #f07aa0 #faf8fd #1d1528 #d42a4c #2f5fd6 #1f8a5a #0a0710 #3a2566 #221a2e',
  ),
  T(
    'riviera',
    'Riviera',
    'Turkoosi veka, laivastonsininen reunus ja sitruunankeltainen.',
    14,
    '#eef3f4 #ffffff #f5f9f9 #d3e0e2 #13262e #43606b #69838d #117a7c #168d8f #0c5b5d #1f3552 #2f4c72 #e8b422 #fcf1cc #0f6a6c #ffffff #1f7a4f #c0392b #fffef9 #13262e #d0392b #1f63c7 #1f8a55 #e6eff0 #d3f0ee #ffffff',
    '#0a1417 #101e22 #15272c #203a40 #e5f1f2 #a6c2c7 #7c9ba1 #13898b #199c9e #0e6a6c #14243a #243a58 #f5c53f #3b3217 #3cbcbe #04191a #6fd39c #f07d74 #f7fbfb #13262e #d0392b #1f63c7 #1f8a55 #071012 #17504f #172a2f',
  ),
  T(
    'grafiitti',
    'Grafiitti',
    'Hiilenharmaa veka, teräs ja huomiovärin oranssi.',
    4,
    '#eceeef #ffffff #f5f6f7 #d4d8db #16191c #474d53 #6d7379 #2f3538 #3a4145 #22272a #101213 #2a2e31 #e8630f #fde6d6 #16191c #ffffff #1f7a4c #c4271b #ffffff #16191c #d3301f #2863d0 #1f8a4f #e7e9eb #fde6d6 #ffffff',
    '#0d0f10 #16191b #1c2023 #2d3236 #eceff1 #b3b9be #899096 #33393d #3e454a #262b2e #07080a #1d2023 #ff7a2b #3d2615 #f0f2f3 #111315 #6fd39c #f07d74 #f7f7f7 #16191c #d3301f #2863d0 #1f8a4f #08090a #4a2a14 #1f2326',
  ),
  T(
    'joulu',
    'Joulupokeri',
    'Kuusenvihreä veka, karpalonpunainen reunus ja kulta.',
    12,
    '#eef0ec #ffffff #f6f7f3 #d8ddd3 #1c211b #4c554a #707a6d #1f4d35 #275e41 #173a28 #7a1b25 #962532 #c9a227 #f6efd3 #1f4d35 #ffffff #2f7a47 #b3202c #fffefb #1c211b #c0242f #2a5db0 #2f7a47 #e9ece5 #f3dde0 #ffffff',
    '#0e120f #161c17 #1b231d #2c372e #eef2ec #b8c2b5 #8d988a #25603f #2d704a #1b4830 #5c1119 #7a1b25 #e2bd4f #3a3218 #4fae7a #07140c #72cf98 #f07b83 #fbfaf5 #1c211b #c0242f #2a5db0 #2f7a47 #0a0d0a #4d1d24 #1f2720',
  ),
  T(
    'kesayo',
    'Kesäyö',
    'Hämärän liila veka, koivunvaalea reunus ja keskiyön aurinko.',
    18,
    '#efeef4 #ffffff #f7f6fa #dddbe7 #1f1d2b #514d66 #76728a #5a4f87 #6a5e9c #443b68 #d9d4c7 #efebe1 #d49a3a #f8ecd7 #4f457a #ffffff #2f7a55 #b8324a #fffefd #1f1d2b #c93a50 #3a62c9 #2f8a5c #e9e8f1 #e3def5 #ffffff',
    '#100f17 #181722 #1e1c2a #302d42 #eeedf5 #bbb7cf #918ca8 #62569a #7064ad #4b4178 #bdb6a6 #d8d2c3 #eab45a #3b3120 #a79ae6 #151226 #72d3a0 #f07d93 #faf9fd #1f1d2b #c93a50 #3a62c9 #2f8a5c #0b0a12 #3a3160 #211f2e',
  ),
  T(
    'saloon',
    'Saloon',
    'Oliivinvihreä veka, tammireunus ja kuparin hohto.',
    4,
    '#ecebe8 #fffdfa #f6f3ee #ddd6cb #26201a #5a4f43 #7e7263 #5e5a2a #6f6a33 #47431f #3b2415 #5a3822 #b86b2b #f5e2d0 #5a3822 #ffffff #3d7a3a #a8321f #fffaf0 #26201a #b83a26 #2f5c9e #3d7a3a #e8e4dd #efe1cf #fffdfa',
    '#14110d #1d1914 #241f19 #3a3228 #f2ede5 #c7bcad #9b8f7f #6c6731 #7c763a #524e24 #2a190e #45291a #e0904a #3d2918 #d9a06a #1c1107 #86cf7f #f08a70 #fbf6ec #26201a #b83a26 #2f5c9e #3d7a3a #0f0d0a #4a321c #29231c',
  ),
  T(
    'tokio',
    'Tokio',
    'Yönsininen veka, tuhkanmusta reunus ja neonpunainen.',
    8,
    '#eeeff3 #ffffff #f5f6f9 #d7dae3 #111522 #434a5e #697086 #1b2238 #252e4a #121829 #0b0d14 #20243a #e8364f #fde1e5 #d42a43 #ffffff #1f8a5f #c21f38 #ffffff #111522 #e0283f #2e63e0 #1b9463 #e8eaf0 #fde1e5 #ffffff',
    '#07080d #0f121b #141827 #242a3d #edf0f7 #adb4c8 #838ba3 #1d2540 #283257 #141a2e #040509 #1a1e30 #ff4d64 #3d1a20 #ff5a70 #1a0509 #5fd6a0 #ff7a8a #f7f8fc #111522 #e0283f #2e63e0 #1b9463 #05060a #4a1a24 #181c2b',
  ),
  T(
    'merituuli',
    'Merituuli',
    'Merenvihreä veka, ajopuureunus ja korallin korostus.',
    16,
    '#ecf2f1 #ffffff #f4f8f7 #d2dfdc #142523 #43605b #68837e #3f8a7e #4a9c8f #2f6c62 #8a7a66 #a8977f #e06a55 #fbe3dd #2f6c62 #ffffff #25805a #c0392b #fffffe #142523 #d4413a #2f67c4 #25805a #e4edeb #d5eee8 #ffffff',
    '#0a1312 #111d1b #162623 #233935 #e6f2ef #a9c4be #7e9b95 #3f8a7e #4a9c8f #2f6c62 #5e5242 #7a6b57 #f28a76 #3d2320 #6cc4b5 #06181a #6fd6a4 #f07d74 #f5fbfa #142523 #d4413a #2f67c4 #25805a #071010 #1c4a42 #162522',
  ),
  T(
    'suklaa',
    'Suklaa',
    'Kaakaonruskea veka, espressoreunus ja kinuskin sävy.',
    10,
    '#eeebe9 #ffffff #f7f4f2 #ded6d1 #231915 #5a4a42 #7e6d64 #4b2e22 #5c3a2b #371f16 #1e1410 #3a2820 #d0924a #f7e7d3 #4b2e22 #ffffff #3a7a4a #b3302a #fffbf6 #231915 #c23a2e #2d5fa8 #3a7a4a #ebe6e3 #f2e2d2 #ffffff',
    '#120d0b #1c1512 #231a16 #382a24 #f3ece8 #c9b9b0 #9d8b81 #5a3829 #6b4332 #432a1f #0c0806 #2a1d17 #e8a95e #3d2c1a #d9a57a #1c0f08 #7fcf98 #f08070 #fbf6f1 #231915 #c23a2e #2d5fa8 #3a7a4a #0d0907 #4a3020 #2a201b',
  ),
  T(
    'kontrasti',
    'Kontrasti',
    'Mustaa valkoisella ja kirkas keltainen: helpoin lukea.',
    6,
    '#ffffff #ffffff #f0f0f0 #6b6b6b #000000 #1f1f1f #3d3d3d #0b3d91 #1049a8 #072c6b #000000 #1f1f1f #ffcc00 #fff4bf #000000 #ffffff #006b2e #b00020 #ffffff #000000 #c8001e #0047c2 #006b2e #f0f0f0 #fff4bf #ffffff',
    '#000000 #0a0a0a #161616 #8a8a8a #ffffff #e6e6e6 #c2c2c2 #0d47a1 #1356c0 #08306e #000000 #262626 #ffd400 #332b00 #ffffff #000000 #4ee07e #ff6b7d #ffffff #000000 #c8001e #0047c2 #006b2e #000000 #3a3000 #1a1a1a',
  ),
];

/** New players (and anyone who has not chosen) get this one. */
export const DEFAULT_THEME = 'saloon';
/** The look app.css paints by itself, without any theme variables. */
export const BASE_THEME = 'nykyinen';
export const THEME_IDS = THEMES.map((t) => t.id) as [string, ...string[]];

export function findTheme(id: string | null | undefined): Theme {
  return THEMES.find((t) => t.id === id) ?? THEMES.find((t) => t.id === DEFAULT_THEME)!;
}

export type ColorMode = 'system' | 'light' | 'dark';
export const COLOR_MODES: readonly ColorMode[] = ['system', 'light', 'dark'];
